import { jwtDecode } from 'jwt-decode';

import { Capacitor } from '@capacitor/core';
import { SecurePreferences } from '@capawesome-team/capacitor-secure-preferences';

import { environment } from '../environment/environment';
import type { TokenValue } from '../pages/advocate/AdvocateAuthSlice';
import { AuthResult } from '../pages/advocate/AdvocateAuthSlice';
import { CryptoService } from './crypto';
import { RaygunErrorHandlerService } from './raygun';

const { logError } = RaygunErrorHandlerService();

export const getStoredAuthAsync = async () => {
  if (!Capacitor.isPluginAvailable('SecurePreferences')) {
    return new AuthResult();
  }

  const ret = await SecurePreferences.get({ key: environment.authResultKey });
  const encryptedAuthResult = ret.value;
  if (encryptedAuthResult) {
    try {
      const decrypted = await CryptoService.decrypt(encryptedAuthResult, environment.cryptoKey, 'getWebUserAsync');
      const authResult: AuthResult = JSON.parse(decrypted);
      return authResult?.token ? authResult : new AuthResult();
    } catch (error) {
      logError(error, ['service/login', 'getWebUserAsync']);
      return new AuthResult();
    }
  }
  return new AuthResult();
};

export const removeStoredAuthAsync = async () => {
  if (!Capacitor.isPluginAvailable('SecurePreferences')) {
    return;
  }

  await SecurePreferences.remove({ key: environment.authResultKey });
};

export const storeAuthAsync = async (user: AuthResult) => {
  if (!Capacitor.isPluginAvailable('SecurePreferences')) {
    return;
  }

  if (user?.token) {
    const encryptedAuthResult = await CryptoService.encrypt(JSON.stringify(user), environment.cryptoKey, 'saveWebUser');
    await SecurePreferences.set({
      key: environment.authResultKey,
      value: encryptedAuthResult,
    });
  }
};

export const decodeToken = (authResult: AuthResult) => {
  try {
    if (authResult?.token) {
      return jwtDecode<TokenValue>(authResult.token);
    }
  } catch (error) {
    logError(error, ['service/login', 'decodeToken']);
  }
};
